import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    Language as LanguageIcon,
    Public as PublicIcon,
    CheckCircle as CheckCircleIcon
} from '@material-ui/icons';
import UserGameTagsManager from './UserGameTagsManager';
import StreamerGroupManager from './StreamerGroupManager';
// 使用现有的语言文件
import {
    getCommonLanguageOptions,
    formatLanguageDisplay,
    detectBrowserLanguage,
    LANGUAGE_MAP,
    getMultilingualText
} from '../../../utils/languages';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#1a2035',
        padding: 0,
        margin: 0,
        overflow: 'visible',
    },
    container: {
        width: '100%',
        minHeight: '100%',
        backgroundColor: '#1a2035',
        padding: theme.spacing(3),
        margin: 0,
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    title: {
        color: '#e0e0e0',
        fontWeight: 500,
        flex: 1,
        minWidth: '300px',
    },
    // 优化语言选择器样式
    languageSelector: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        minWidth: '280px',
        padding: theme.spacing(1, 2),
        borderRadius: 12,
        background: 'linear-gradient(135deg, rgba(100, 181, 246, 0.08), rgba(63, 81, 181, 0.05))',
        border: '1px solid rgba(100, 181, 246, 0.2)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(100, 181, 246, 0.12), rgba(63, 81, 181, 0.08))',
            borderColor: 'rgba(100, 181, 246, 0.4)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(100, 181, 246, 0.2)',
        },
    },

    languageIcon: {
        fontSize: '20px',
        color: '#64b5f6',
        transition: 'all 0.3s ease',
        filter: 'drop-shadow(0 0 4px rgba(100, 181, 246, 0.3))',
    },

    languageControl: {
        minWidth: 160,
        flex: 1,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#e0e0e0',
            height: '38px',
            borderRadius: 8,
            transition: 'all 0.3s ease',
            '& fieldset': {
                borderColor: 'rgba(100, 181, 246, 0.3)',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(100, 181, 246, 0.5)',
                boxShadow: '0 0 8px rgba(100, 181, 246, 0.2)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#64b5f6',
                borderWidth: '2px',
                boxShadow: '0 0 12px rgba(100, 181, 246, 0.3)',
            },
        },
        '& .MuiInputLabel-root': {
            color: '#b0b0b0',
            fontSize: '14px',
            fontWeight: 500,
            transform: 'translate(14px, 10px) scale(1)',
        },
        '& .MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75)',
            color: '#64b5f6',
        },
        '& .MuiSelect-icon': {
            color: '#64b5f6',
            transition: 'transform 0.3s ease',
        },
        '& .MuiSelect-iconOpen': {
            transform: 'rotate(180deg)',
        },
    },

    languageMenuItem: {
        padding: theme.spacing(1.5, 2),
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(100, 181, 246, 0.1)',
            transform: 'translateX(4px)',
        },
        '&.Mui-selected': {
            backgroundColor: 'rgba(100, 181, 246, 0.15)',
            borderLeft: '3px solid #64b5f6',
            '&:hover': {
                backgroundColor: 'rgba(100, 181, 246, 0.2)',
            },
        },
    },

    languageMenuPaper: {
        backgroundColor: '#232b42',
        border: '1px solid rgba(100, 181, 246, 0.2)',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(100, 181, 246, 0.1)',
        backdropFilter: 'blur(20px)',
        marginTop: theme.spacing(1),
        '& .MuiList-root': {
            padding: theme.spacing(1),
        },
    },

    languageFlag: {
        width: 20,
        height: 20,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#64b5f6',
        color: 'white',
        fontSize: '11px',
        fontWeight: 700,
        marginRight: theme.spacing(1),
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },

    languageInfo: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },

    languageName: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#e0e0e0',
        lineHeight: 1.2,
    },

    languageNative: {
        fontSize: '12px',
        color: '#b0b0b0',
        lineHeight: 1.2,
        marginTop: '2px',
    },

    languageCode: {
        fontSize: '10px',
        color: '#888',
        fontFamily: 'monospace',
        backgroundColor: 'rgba(100, 181, 246, 0.1)',
        padding: '2px 6px',
        borderRadius: 4,
        marginLeft: theme.spacing(1),
        fontWeight: 600,
        letterSpacing: '0.5px',
    },

    currentLanguageStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1),
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderRadius: 6,
        border: '1px solid rgba(76, 175, 80, 0.3)',
    },

    statusIcon: {
        fontSize: '12px',
        color: '#4caf50',
    },

    statusText: {
        fontSize: '11px',
        color: '#4caf50',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },

    // 添加响应式支持
    [theme.breakpoints.down('md')]: {
        languageSelector: {
            minWidth: '240px',
            padding: theme.spacing(0.8, 1.5),
        },
        languageControl: {
            minWidth: 140,
        },
    },
    [theme.breakpoints.down('sm')]: {
        languageSelector: {
            minWidth: '200px',
            gap: theme.spacing(1),
            padding: theme.spacing(0.6, 1),
        },
        languageControl: {
            minWidth: 120,
        },
    },
}));

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`streamer-package-tabpanel-${index}`}
            aria-labelledby={`streamer-package-tab-${index}`}
            style={{ width: '100%', padding: 0, margin: 0 }}
            {...other}
        >
            {value === index && (
                <Box style={{ width: '100%', padding: 0, margin: 0 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function StreamerPackageManager() {
    const classes = useStyles();
    const [currentTab, setCurrentTab] = useState(0);

    // 改进语言状态初始化
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        try {
            // 1. 优先从本地存储获取
            const savedLanguage = localStorage.getItem('preferredLanguage');
            if (savedLanguage && ['en', 'zh'].includes(savedLanguage)) {
                return savedLanguage;
            }
            
            // 2. 自动检测浏览器语言
            const detectedLanguage = detectBrowserLanguage();
            console.log('Detected browser language:', detectedLanguage);
            
            // 3. 保存检测到的语言
            localStorage.setItem('preferredLanguage', detectedLanguage);
            return detectedLanguage;
        } catch (error) {
            console.error('Error initializing language:', error);
            return 'en';
        }
    });
    const [languageOptions] = useState(getCommonLanguageOptions());

    // 监听语言变化并同步到本地存储
    useEffect(() => {
        localStorage.setItem('preferredLanguage', selectedLanguage);
        console.log('Language changed to:', selectedLanguage);
    }, [selectedLanguage]);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    // 修复语言变化处理
    const handleLanguageChange = (event) => {
        const newLanguage = event.target.value;
        if (['en', 'zh'].includes(newLanguage)) {
            setSelectedLanguage(newLanguage);
            localStorage.setItem('preferredLanguage', newLanguage);
        }
    };

    // 获取当前语言显示信息
    const getCurrentLanguageInfo = () => {
        return LANGUAGE_MAP[selectedLanguage] || { name: 'English', local: 'English' };
    };

    // 获取多语言文本
    const getText = (key, params = {}) => {
        return getMultilingualText(key, selectedLanguage, params);
    };

    // 更新渲染语言选择器函数
    const renderLanguageSelector = () => {
        // 获取语言国旗emoji映射
        const getLanguageFlag = (langCode) => {
            const flagMap = {
                'en': '🇺🇸',
                'zh': '🇨🇳',
            };
            return flagMap[langCode] || '🌐';
        };

        return (
            <Box className={classes.languageSelector}>
                <Tooltip
                    title={getText('languageSettings')}
                    placement="bottom"
                    arrow
                >
                    <LanguageIcon className={classes.languageIcon} />
                </Tooltip>

                <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.languageControl}
                >
                    <InputLabel>{getText('interfaceLanguage')}</InputLabel>
                    <Select
                        value={selectedLanguage}
                        onChange={handleLanguageChange}
                        label={getText('interfaceLanguage')}
                        MenuProps={{
                            PaperProps: {
                                className: classes.languageMenuPaper,
                            },
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                        }}
                    >
                        {languageOptions.map((lang) => (
                            <MenuItem
                                key={lang.value}
                                value={lang.value}
                                className={classes.languageMenuItem}
                                selected={selectedLanguage === lang.value}
                            >
                                <Box display="flex" alignItems="center" width="100%">
                                    <Box className={classes.languageFlag}>
                                        {getLanguageFlag(lang.value)}
                                    </Box>

                                    <Box className={classes.languageInfo}>
                                        <Typography className={classes.languageName}>
                                            {lang.label}
                                        </Typography>
                                        {lang.localName !== lang.label && (
                                            <Typography className={classes.languageNative}>
                                                {lang.localName}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box className={classes.languageCode}>
                                        {lang.value.toUpperCase()}
                                    </Box>

                                    {selectedLanguage === lang.value && (
                                        <Box ml={1}>
                                            <CheckCircleIcon
                                                style={{
                                                    fontSize: '16px',
                                                    color: '#4caf50'
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* 当前语言状态指示器 */}
                <Box className={classes.currentLanguageStatus}>
                    <PublicIcon className={classes.statusIcon} />
                    <Typography className={classes.statusText}>
                        {getCurrentLanguageInfo().local}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <div className={classes.root}>
            <Box className={classes.container}>
                <div className={classes.header}>
                    <Typography variant="h4" className={classes.title}>
                        {getText('streamerPackageSystem')}
                        <Typography variant="caption" style={{
                            display: 'block',
                            color: '#b0b0b0',
                            marginTop: 4
                        }}>
                            {getText('interfaceLanguage')}: {formatLanguageDisplay(selectedLanguage, true)}
                        </Typography>
                    </Typography>

                    {/* 语言选择器 */}
                    {renderLanguageSelector()}
                </div>

                <Paper className={classes.tabRoot}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        <Tab label={getText('streamerFilterSystem')} />
                        <Tab label={getText('streamerGroupManagement')} />
                    </Tabs>
                </Paper>

                <div className={classes.tabContent}>
                    <TabPanel value={currentTab} index={0}>
                        <UserGameTagsManager
                            selectedLanguage={selectedLanguage}
                            onLanguageChange={setSelectedLanguage}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={1}>
                        <StreamerGroupManager
                            selectedLanguage={selectedLanguage}
                            onLanguageChange={setSelectedLanguage}
                        />
                    </TabPanel>
                </div>
            </Box>
        </div>
    );
}